// Microbe — one-shot v1→v2 save migration. This is the ONLY module that knows the v1 save shape.
//
// v1 was a flat, dive-timer economy: currencies + a `specs` map at the top level, plus dive-run
// bookkeeping (divesDone/bestFood) that v2 drops entirely. v2 nests currencies under `wallet`,
// renames `specs`→`genome` (same trait ids, so it's a straight lift), resets mass, and stamps
// lifetime `stats`. migrate() runs ONCE at boot, BEFORE any module reads state, and is the sole
// place the old shape is referenced. Everything is wrapped so a migration fault can never brick
// boot: on ANY error we fall back to a clean, fresh v2 save via save.reset(). A brand-new player
// (empty storage → DEFAULTS, already version 2) is a no-op and ends valid.

import { save, DEFAULTS, MASS_START } from './state.js';
import { SAVE_VERSION } from './types.js';
import type { MicrobeState, Genome, TraitId, Wallet } from './types.js';

/** The v1 spec map — flat trait levels. Keys line up 1:1 with v2 TraitIds, so it lifts directly. */
interface V1Specs {
  photosynthesis?: number;
  o2Tolerance?: number;
  co2Harvesting?: number;
  motility?: number;
  membrane?: number;
  fermentation?: number;
}

/** The v1 persisted shape (flat currencies + specs + dive bookkeeping). All fields optional so a
 *  partial/corrupt old save reads through with fallbacks instead of throwing. */
interface V1Save {
  version?: number;
  energy?: number;
  biomass?: number;
  xp?: number;
  enzymes?: number;
  specs?: V1Specs;
  lastSeen?: number;
  soundOn?: boolean;
  divesDone?: number; // dropped in v2
  bestFood?: number; // dropped in v2
}

/** The six v1 spec keys, in a stable order, all valid v2 TraitIds. */
const V1_TRAIT_IDS: readonly TraitId[] = [
  'photosynthesis',
  'co2Harvesting',
  'motility',
  'membrane',
  'o2Tolerance',
  'fermentation',
];

/** Finite-number coercion with a fallback — guards NaN/Infinity/undefined from a garbled save. */
function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/**
 * Bring the persisted save up to SAVE_VERSION (2). Idempotent: a save already at v2 (including a
 * brand-new player seeded from DEFAULTS) is left untouched. Otherwise the save is treated as v1
 * (or empty) and reshaped into a valid v2 digest. ANY failure resets to a clean fresh v2 rather
 * than throwing, so a broken migration can never prevent boot.
 */
export function migrate(): void {
  try {
    if (save.value.version === SAVE_VERSION) return;

    // Not v2 → treat as v1/empty. save.value carries the raw v1 fields (createSave spreads the
    // parsed object over DEFAULTS), so read them off an explicitly-typed v1 view.
    const v1 = save.value as unknown as V1Save;

    const wallet: Wallet = {
      energy: num(v1.energy, 0),
      biomass: num(v1.biomass, 0),
      xp: num(v1.xp, 0),
      enzymes: num(v1.enzymes, 0),
    };

    // Lift specs → genome. Absent/zero levels are simply omitted (absent key === level 0).
    const genome: Genome = {};
    const specs = v1.specs;
    if (specs && typeof specs === 'object') {
      for (const id of V1_TRAIT_IDS) {
        const level = num(specs[id], 0);
        if (level > 0) genome[id] = level;
      }
    }

    const next: MicrobeState = {
      version: SAVE_VERSION,
      wallet,
      genome,
      mass: MASS_START,
      lastSeen: num(v1.lastSeen, DEFAULTS.lastSeen),
      soundOn: typeof v1.soundOn === 'boolean' ? v1.soundOn : DEFAULTS.soundOn,
      stats: { maxMass: MASS_START, totalEaten: 0, born: Date.now() },
    };

    save.patch(next);
  } catch {
    // A broken migrate must never brick boot — fall back to a clean, fresh v2 save.
    save.reset();
  }
}
